import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { ModalContext } from "@/shared/ui/overlay/modal-context";

type ModalStackEntry = [
  id: number,
  depth: number,
  closeHandler: () => void,
  lockScroll: boolean,
];

type BodyScrollLockSnapshot = [
  scrollY: number,
  bodyStyle: string,
  rootOverflow: string,
  fixedBody: boolean,
];

export const ModalProvider: React.FunctionComponent<{
  children: ReactNode;
  fixedBodyScrollLock?: boolean;
}> = ({
  children,
  fixedBodyScrollLock = true,
}: {
  children: ReactNode;
  fixedBodyScrollLock?: boolean;
}) => {
  const modalStackRef = useRef<ModalStackEntry[]>([]);
  const [, notifyModalStackChanged] = useReducer(
    (version: number): number => version + 1,
    0,
  );
  const nextModalIdRef = useRef(0);
  const bodyScrollLockSnapshotRef = useRef<BodyScrollLockSnapshot | null>(null);
  const fixedBodyScrollLockRef = useRef(fixedBodyScrollLock);
  fixedBodyScrollLockRef.current = fixedBodyScrollLock;

  const syncBodyScrollLock = useCallback((): void => {
    const shouldLock = modalStackRef.current.some((entry) => entry[3]);
    if (shouldLock && !bodyScrollLockSnapshotRef.current) {
      const { style } = document.body;
      const rootStyle = document.documentElement.style;
      const scrollY = window.scrollY;
      const fixedBody = fixedBodyScrollLockRef.current;
      bodyScrollLockSnapshotRef.current = [
        scrollY,
        style.cssText,
        rootStyle.getPropertyValue("overflow"),
        fixedBody,
      ];
      if (!fixedBody) {
        // PCのwindow仮想リストが現在行を維持できるよう、実スクロール座標は動かさない。
        rootStyle.overflow = "hidden";
      } else {
        // iOSではoverflowだけでは背景スクロールを防げないためbodyを固定する。
        style.cssText += `;overflow:hidden;position:fixed;top:${-scrollY}px;left:0;right:0;width:auto`;
      }
    } else if (!shouldLock && bodyScrollLockSnapshotRef.current) {
      const [scrollY, bodyStyle, rootOverflow, fixedBody] =
        bodyScrollLockSnapshotRef.current;
      bodyScrollLockSnapshotRef.current = null;
      if (fixedBody) {
        document.body.style.cssText = bodyStyle;
        window.scrollTo(0, scrollY);
      } else if (rootOverflow) {
        document.documentElement.style.overflow = rootOverflow;
      } else {
        document.documentElement.style.removeProperty("overflow");
      }
    }
  }, []);

  const registerModal = useCallback(
    (
      closeHandler: () => void,
      { lockBodyScroll = true } = {},
    ): { id: number; depth: number } => {
      const id = nextModalIdRef.current;
      nextModalIdRef.current += 1;
      const depth = (modalStackRef.current.at(-1)?.[1] ?? -1) + 1;
      modalStackRef.current.push([id, depth, closeHandler, lockBodyScroll]);
      notifyModalStackChanged();
      syncBodyScrollLock();
      return { id, depth };
    },
    [syncBodyScrollLock],
  );

  const unregisterModal = useCallback(
    (id: number): void => {
      modalStackRef.current = modalStackRef.current.filter(
        (entry) => entry[0] !== id,
      );
      notifyModalStackChanged();
      syncBodyScrollLock();
    },
    [syncBodyScrollLock],
  );

  const closeAllModals = useCallback((): void => {
    const entries = modalStackRef.current;
    modalStackRef.current = [];
    notifyModalStackChanged();
    syncBodyScrollLock();
    for (const entry of entries.reverse()) {
      entry[2]();
    }
  }, [syncBodyScrollLock]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape" || event.isComposing) return;
      const topmostModal = modalStackRef.current.at(-1);
      if (!topmostModal) return;
      event.preventDefault();
      topmostModal[2]();
    };
    window.addEventListener("keydown", handleKeyDown);
    return (): void => {
      window.removeEventListener("keydown", handleKeyDown);
      modalStackRef.current = [];
      syncBodyScrollLock();
    };
  }, [syncBodyScrollLock]);

  const modalDepth = modalStackRef.current.length;
  const contextValue = useMemo(
    () => ({
      modalDepth,
      isModalOpen: modalDepth > 0,
      registerModal,
      unregisterModal,
      closeAllModals,
    }),
    [closeAllModals, modalDepth, registerModal, unregisterModal],
  );

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
    </ModalContext.Provider>
  );
};
